import { useState } from "react";
import { Sparkles, Download } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Cost, CopyButton, PageHeader, Section, Spinner } from "../components/AiHtml";
import { useGenerate } from "../lib/useGenerate";

const STARTER = `<!DOCTYPE html>\n<html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{font-family:sans-serif;background:#0B1120;color:#fff;display:grid;place-items:center;height:100vh;margin:0}h1{color:#FF6B00}</style></head>\n<body><h1>Describe your site and click Generate</h1></body></html>`;

const HOSTS = {
  Hostinger: ["Log in to hPanel and open Websites → Manage.", "Go to Files → File Manager → public_html.", "Delete the default index file and upload your index.html.", "Connect your domain under Domains and enable free SSL.", "Visit your domain — the site is live."],
  Vercel: ["Create a free account at vercel.com.", "Put index.html in a new folder (or GitHub repo).", "Click Add New → Project, then import the repo or drag the folder.", "Keep framework preset as Other and click Deploy.", "Add your custom domain in Project → Settings → Domains."],
  Netlify: ["Sign up at netlify.com.", "Open Sites → Add new site → Deploy manually.", "Drag and drop the folder containing index.html.", "Netlify gives you a live URL instantly.", "Set your custom domain in Domain management and enable HTTPS."],
};

export default function WebsiteBuilder() {
  const [prompt, setPrompt] = useState("");
  const [code, setCode] = useState(STARTER);
  const g = useGenerate("website");
  const generate = async () => {
    const r = await g.run(prompt);
    if (r?.html) setCode(r.html);
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([code], { type: "text/html" }));
    a.download = "index.html";
    a.click();
  };
  return (
    <div className="space-y-6">
      <PageHeader label="Website Builder" title="Prompt to landing page" desc="Edit the code on the left and see the live preview update instantly." />
      <div className="card-n p-5 space-y-4">
        <Textarea data-testid="website-prompt-input" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Landing page for a Pune yoga studio with pricing, testimonials and contact form" />
        <div className="flex items-center gap-3"><Cost n={15} /><Button data-testid="website-generate-btn" disabled={g.busy || !prompt.trim()} onClick={generate} className="rounded-full"><Sparkles size={16} className="mr-1.5" /> Generate site</Button></div>
      </div>
      {g.busy ? <div className="card-n"><Spinner label="Coding your website... (up to 60s)" /></div> : (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="index.html" right={<div className="flex gap-2"><CopyButton testId="website-copy-btn" text={code} /><Button size="sm" variant="outline" onClick={download} data-testid="website-download-btn" className="rounded-full h-8"><Download size={13} className="mr-1" />Download</Button></div>}>
            <textarea data-testid="website-code-editor" value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false} className="w-full h-[560px] bg-[#070B14] text-emerald-200 font-mono text-xs p-4 rounded-lg border border-slate-800 focus:outline-none focus:border-orange-500 scroll-thin resize-none" />
          </Section>
          <Section title="Live preview">
            <iframe data-testid="website-preview-frame" title="preview" srcDoc={code} sandbox="allow-scripts" className="w-full h-[560px] bg-white rounded-lg" />
          </Section>
        </div>
      )}
      <Section title="Host your site" testId="host-guide">
        <Tabs defaultValue="Hostinger">
          <TabsList className="bg-slate-900 border border-slate-800">{Object.keys(HOSTS).map((h) => <TabsTrigger key={h} value={h} data-testid={`host-tab-${h.toLowerCase()}`} className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">{h}</TabsTrigger>)}</TabsList>
          {Object.entries(HOSTS).map(([h, steps]) => (
            <TabsContent key={h} value={h}><ol className="mt-4 space-y-2">{steps.map((s, i) => <li key={i} className="flex gap-3 text-sm text-slate-300"><span className="w-6 h-6 shrink-0 rounded-full bg-orange-500/15 text-orange-400 grid place-items-center text-xs font-mono">{i + 1}</span>{s}</li>)}</ol></TabsContent>
          ))}
        </Tabs>
      </Section>
    </div>
  );
}
